import type { Source, SourceType } from '../types';

export type FilePreviewKind =
  | 'image'
  | 'csv'
  | 'tsv'
  | 'xlsx'
  | 'docx'
  | 'text'
  | 'download-only';

const TEXT_EXTENSIONS = new Set(['.txt', '.md']);
// mammoth (docx-parser.ts) only supports the OOXML `.docx` format — legacy
// binary `.doc` (OLE2) is NOT included here and falls through to
// download-only, same as an unparseable spreadsheet (OMNI-839).
const DOCX_EXTENSIONS = new Set(['.docx']);
// Text-ish formats only generated artifacts produce; kept separate from
// TEXT_EXTENSIONS so the citation path's behaviour is unchanged.
const ARTIFACT_TEXT_EXTENSIONS = new Set([
  '.json',
  '.html',
  '.log',
  '.yaml',
  '.yml',
]);
const IMAGE_EXTENSIONS = new Set([
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.svg',
]);

/** Exported so callers that need the raw extension for presentation
 *  purposes (e.g. `FilePreviewPanel`'s download-card file-type icon) don't
 *  have to re-implement this parsing themselves. */
export function extensionOf(filename: string): string {
  const name = (filename || '').trim().toLowerCase();
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot);
}

/** True when the filename's extension is one a browser renders in an `<img>`. */
export function isImageFilename(filename: string): boolean {
  return IMAGE_EXTENSIONS.has(extensionOf(filename));
}

/** Resolve how a citation's file should be previewed, from its backend
 *  `type` bucket plus the filename extension in `title` — mirrors the
 *  backend's own extension-based dispatch (chat-agent's
 *  `document_formats.py`), since the wire payload carries no separate
 *  mime-type field.
 *
 * Every `spreadsheet`-typed source (`.xlsx`, `.xls`, `.xlsm`, `.xlsb`, `.csv`,
 * `.tsv`, ...) always resolves to `'download-only'` (OMNI-838). `.xlsx` table
 * preview was working, but `.xls`/`.xlsm`/`.xlsb` were either silently
 * misrouted to the `exceljs`/OOXML-only parser (`.xls`) or fell through to a
 * generic, unhandled `document` bucket (`.xlsm`/`.xlsb`) — broken or blank
 * previews instead of a clean fallback. Rather than teach the parser every
 * binary/legacy Excel variant, the whole spreadsheet family now gets the
 * same safe, consistent outcome: a download card, no preview attempt. */
export function resolveFilePreviewKind(
  source: Pick<Source, 'type' | 'title'>
): FilePreviewKind {
  if (source.type === 'image') return 'image';
  if (source.type === 'artifact') return resolveArtifactKind(source.title);
  if (source.type === 'spreadsheet') return 'download-only';
  if (source.type === 'document') {
    const ext = extensionOf(source.title);
    if (TEXT_EXTENSIONS.has(ext)) return 'text';
    if (DOCX_EXTENSIONS.has(ext)) return 'docx';
  }
  return 'download-only';
}

/** Files the agent generated in this conversation, resolved purely by
 *  extension — there is no backend `type` bucket to consult and no upload to
 *  distrust.
 *
 *  Unlike the citation path above, `.xlsx`/`.csv`/`.tsv` reach the table
 *  renderer here. The OMNI-838 blanket spreadsheet downgrade guards against
 *  *uploaded* legacy variants (`.xls`/`.xlsm`/`.xlsb`) reaching the OOXML-only
 *  parser; the code-exec sandbox only ever writes `.xlsx` (xlsxwriter/openpyxl)
 *  and delimited text, so that risk isn't present. Legacy variants still land
 *  on `download-only` below if one ever appears.
 *
 *  Anything unrecognised falls through to `download-only`, which renders as a
 *  tidy "can't preview this inline" card rather than an error — so a new output
 *  format degrades gracefully instead of breaking the panel. */
function resolveArtifactKind(filename: string): FilePreviewKind {
  if (isImageFilename(filename)) return 'image';
  const ext = extensionOf(filename);
  if (ext === '.csv') return 'csv';
  if (ext === '.tsv') return 'tsv';
  if (ext === '.xlsx') return 'xlsx';
  if (TEXT_EXTENSIONS.has(ext) || ARTIFACT_TEXT_EXTENSIONS.has(ext))
    return 'text';
  if (DOCX_EXTENSIONS.has(ext)) return 'docx';
  return 'download-only';
}

/** True for the three backend "uploaded document" buckets this ticket adds
 *  handling for (mirrors chat-agent's `_UPLOADED_DOC_TYPES`, minus `pdf`,
 *  which already has its own established handling). */
export function isFilePreviewSourceType(type: SourceType): boolean {
  return type === 'spreadsheet' || type === 'document' || type === 'image';
}
